import React, { useEffect, useRef, useState } from 'react'

import DOMPurify from 'dompurify'

import sendRequest from '../../services/api/sendRequest'
import Permissions, { CAPABILITIES } from '../../utils/Permissions'
import FAIcon from '../FAIcon'

// Helper for detecting empty Quill rich-text markup per AGENTS.md rules
export function isQuillEmpty(html) {
  if (!html) return true
  const stripped = html.replace(/<[^>]*>/g, '').trim()
  return stripped.length === 0
}

const CRC32_TABLE = (() => {
  const table = new Uint32Array(256)
  for (let i = 0; i < 256; i += 1) {
    let c = i
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    }
    table[i] = c >>> 0
  }
  return table
})()

export function crc32Bytes(bytes) {
  let crc = 0xffffffff
  for (let i = 0; i < bytes.length; i += 1) {
    crc = (crc >>> 8) ^ CRC32_TABLE[(crc ^ bytes[i]) & 0xff]
  }
  return (crc ^ 0xffffffff) >>> 0
}

function encodeUtf8(str) {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(str)
  }
  const arr = new Uint8Array(str.length)
  for (let i = 0; i < str.length; i += 1) {
    arr[i] = str.charCodeAt(i) & 0xff
  }
  return arr
}

function decodeUtf8(bytes) {
  if (typeof TextDecoder !== 'undefined') {
    return new TextDecoder('utf-8').decode(bytes)
  }
  let out = ''
  for (let i = 0; i < bytes.length; i += 1) {
    out += String.fromCharCode(bytes[i])
  }
  return out
}

function encodeEventStreamHeader(name, value) {
  const nameBytes = encodeUtf8(name)
  const valBytes = encodeUtf8(value)
  const out = new Uint8Array(1 + nameBytes.length + 1 + 2 + valBytes.length)
  const view = new DataView(out.buffer)
  let offset = 0
  view.setUint8(offset, nameBytes.length)
  offset += 1
  out.set(nameBytes, offset)
  offset += nameBytes.length
  view.setUint8(offset, 7) // 7 = string header value
  offset += 1
  view.setUint16(offset, valBytes.length, false)
  offset += 2
  out.set(valBytes, offset)
  return out
}

export function encodeTranscribeAudioEvent(pcmBytes) {
  const h1 = encodeEventStreamHeader(':content-type', 'application/octet-stream')
  const h2 = encodeEventStreamHeader(':event-type', 'AudioEvent')
  const h3 = encodeEventStreamHeader(':message-type', 'event')
  const headersLen = h1.length + h2.length + h3.length
  const payload = pcmBytes instanceof Uint8Array ? pcmBytes : new Uint8Array(pcmBytes || 0)
  const totalLen = 12 + headersLen + payload.length + 4
  const msg = new Uint8Array(totalLen)
  const view = new DataView(msg.buffer)

  view.setUint32(0, totalLen, false)
  view.setUint32(4, headersLen, false)
  view.setUint32(8, crc32Bytes(msg.subarray(0, 8)), false)

  let offset = 12
  msg.set(h1, offset)
  offset += h1.length
  msg.set(h2, offset)
  offset += h2.length
  msg.set(h3, offset)
  offset += h3.length
  msg.set(payload, offset)

  view.setUint32(totalLen - 4, crc32Bytes(msg.subarray(0, totalLen - 4)), false)
  return msg.buffer
}

export function decodeTranscribeEventStreamMessage(arrayBuffer) {
  const bytes = arrayBuffer instanceof Uint8Array ? arrayBuffer : new Uint8Array(arrayBuffer)
  if (bytes.byteLength < 16) return { headers: {}, payload: null }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  const totalLen = view.getUint32(0, false)
  const headersLen = view.getUint32(4, false)
  const headersEnd = Math.min(bytes.byteLength - 4, 12 + headersLen)
  const headers = {}
  let offset = 12

  while (offset < headersEnd) {
    const nameLen = view.getUint8(offset)
    offset += 1
    const name = decodeUtf8(bytes.subarray(offset, offset + nameLen))
    offset += nameLen
    const valType = view.getUint8(offset)
    offset += 1
    if (valType === 7) {
      const valLen = view.getUint16(offset, false)
      offset += 2
      headers[name] = decodeUtf8(bytes.subarray(offset, offset + valLen))
      offset += valLen
    } else {
      break
    }
  }

  const payloadEnd = Math.min(bytes.byteLength - 4, totalLen - 4)
  if (payloadEnd <= headersEnd) return { headers, payload: null }
  const payloadStr = decodeUtf8(bytes.subarray(headersEnd, payloadEnd))
  try {
    return { headers, payload: payloadStr ? JSON.parse(payloadStr) : null }
  } catch (_e) {
    return { headers, payload: null }
  }
}

export function downsampleFloat32ToInt16Pcm(float32Samples, inputSampleRate, targetSampleRate = 16000) {
  if (!float32Samples || float32Samples.length === 0) return new Uint8Array(0)
  const ratio = inputSampleRate > 0 ? inputSampleRate / targetSampleRate : 1
  const outLength = Math.max(1, Math.round(float32Samples.length / ratio))
  const outBuffer = new ArrayBuffer(outLength * 2)
  const view = new DataView(outBuffer)

  let offsetResult = 0
  let offsetBuffer = 0
  while (offsetResult < outLength) {
    const nextOffsetBuffer = Math.min(float32Samples.length, Math.round((offsetResult + 1) * ratio))
    let accum = 0
    let count = 0
    for (let i = offsetBuffer; i < nextOffsetBuffer; i += 1) {
      accum += float32Samples[i]
      count += 1
    }
    const sample = count > 0 ? accum / count : float32Samples[Math.min(offsetBuffer, float32Samples.length - 1)] || 0
    const clamped = Math.max(-1, Math.min(1, sample))
    view.setInt16(offsetResult * 2, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true)
    offsetResult += 1
    offsetBuffer = nextOffsetBuffer
  }
  return new Uint8Array(outBuffer)
}

// Parse spoken duration phrases into 0.1-hr increments
export function parseSpokenHours(textFragment) {
  if (!textFragment) return 0
  const s = textFragment.toLowerCase().trim()

  const wordMap = {
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
    ten: 10,
    fifteen: 15,
    twenty: 20,
    thirty: 30,
    forty: 40,
    'forty-five': 45,
    'forty five': 45,
    fifty: 50,
    sixty: 60,
    ninety: 90,
  }

  if (s.includes('an hour and a half') || s.includes('hour and a half')) return 1.5
  if (s.includes('half an hour') || s.includes('half hour')) return 0.5
  if (s.includes('quarter of an hour') || s.includes('quarter hour')) return 0.3
  if (s === 'an hour' || s === 'one hour' || s === '1 hour') return 1.0

  const hrMinMatch = s.match(/(\d+)\s*(?:hours?|hrs?)\s*(?:and\s*)?(\d+)\s*(?:minutes?|mins?)/)
  if (hrMinMatch) {
    const hrs = parseFloat(hrMinMatch[1]) || 0
    const mins = parseFloat(hrMinMatch[2]) || 0
    return Math.max(0.1, Math.ceil((hrs + mins / 60) * 10 - 1e-9) / 10)
  }

  const decHrMatch = s.match(/(\d+(?:\.\d+)?)\s*(?:hours?|hrs?)/)
  if (decHrMatch) {
    const hrs = parseFloat(decHrMatch[1]) || 0
    return hrs > 0 ? Math.max(0.1, Math.ceil(hrs * 10 - 1e-9) / 10) : 0
  }

  const minMatch = s.match(/(\d+)\s*(?:minutes?|mins?)/)
  if (minMatch) {
    const mins = parseFloat(minMatch[1]) || 0
    return mins > 0 ? Math.max(0.1, Math.ceil((mins / 60) * 10 - 1e-9) / 10) : 0
  }

  for (const [word, val] of Object.entries(wordMap)) {
    if (s.includes(`${word} min`)) {
      return Math.max(0.1, Math.ceil((val / 60) * 10 - 1e-9) / 10)
    }
    if (s.includes(`${word} hour`) || s.includes(`${word} hr`)) {
      return Math.max(0.1, Math.ceil(val * 10 - 1e-9) / 10)
    }
  }

  return 0
}

// Synthesize a concise, action-oriented task title (3-8 words) from a spoken clause
// so raw transcription sentences never become the task title.
export function summarizeTaskTitle(rawText) {
  if (!rawText) return ''
  let clause = String(rawText).replace(/\s+/g, ' ').trim()

  // If the text is a long run-on sentence starting with visit/travel narrative, isolate the actionable clause
  const actionStartMatch = clause.match(
    /\b(?:i\s+need\s+to|we\s+need\s+to|need\s+to|needs\s+to|must|please|request|obtain|get\s+authorization|authorize|schedule|order|submit|send|transmit|follow\s+up\s+(?:on|to|with)|coordinate|refer|recommend(?:ed)?\s+\d+|requires?\s+\d+|medication\s+dosage\s+review)\b[\s\S]*$/i,
  )
  if (actionStartMatch && (clause.length > 50 || /^(?:attended|spent|travel|conferred\s+with)\b/i.test(clause))) {
    clause = actionStartMatch[0].trim()
  }

  // Strip trailing non-task clauses (e.g., "and conferred with...", "spent 1.2 hours...", "next visit in...")
  clause = clause
    .replace(/\b(?:and\s+)?(?:conferred\s+with|spent\s+\d|travel\s+was|next\s+visit\s+in)\b[\s\S]*$/i, '')
    .replace(/[.!?]+$/, '')
    .trim()

  const lower = clause.toLowerCase()

  // High-confidence clinical task title patterns
  if (/\b(physical therapy|pt)\b/i.test(lower)) {
    if (/\b(auth|authorization|pre-auth|extension)\b/i.test(lower)) {
      return 'Request PT Authorization'
    }
    return 'Coordinate Physical Therapy'
  }
  if (/\b(occupational therapy|ot)\b/i.test(lower)) {
    if (/\b(auth|authorization|pre-auth|extension)\b/i.test(lower)) {
      return 'Request OT Authorization'
    }
    return 'Coordinate Occupational Therapy'
  }
  if (/\b(mri|ct\s+scan|x-?ray|ultrasound|emg|diagnostic|imaging)\b/i.test(lower)) {
    const modMatch = clause.match(/\b(MRI|CT|X-?Ray|EMG|Ultrasound)\b/i)
    const modLabel = modMatch ? modMatch[1].toUpperCase() : 'Diagnostic Imaging'
    if (/\b(auth|authorization|pre-auth)\b/i.test(lower)) {
      return `Request ${modLabel} Authorization`
    }
    if (/\b(result|results|report|review|films)\b/i.test(lower)) {
      return `Follow Up on ${modLabel} Results`
    }
    return `Schedule ${modLabel}`
  }
  if (/\b(medical records?|chart notes?|records?\s+request|office notes?)\b/i.test(lower)) {
    return 'Request Medical Records'
  }
  if (
    /\b(medication|dosage|prescription|pharmacy|refill)\b/i.test(lower) &&
    /\b(review|check|reconcil|auth)\b/i.test(lower)
  ) {
    return 'Medication Dosage Review'
  }
  if (
    /\b(work status|restrictions?|duty)\b/i.test(lower) &&
    /\b(employer|adjuster|carrier|send|transmit|notify|forward)\b/i.test(lower)
  ) {
    return 'Send Work Status Update'
  }
  if (/\b(refer|referral|specialist|consult)\b/i.test(lower)) {
    return 'Coordinate Specialist Referral'
  }

  // General cleanup: strip conversational lead-ins and normalize verb
  let cleaned = clause
    .replace(
      /^(?:and\s+|also\s+|then\s+)?(?:i\s+need\s+to|we\s+need\s+to|need\s+to|needs\s+to|i\s+must|we\s+must|must|i\s+have\s+to|we\s+have\s+to|please|make\s+sure\s+to|remember\s+to|patient\s+(?:needs|requires)|doctor\s+(?:recommended|ordered|wants)|dr\.?\s+[a-z]+\s+(?:recommended|ordered|wants))\s+/i,
      '',
    )
    .replace(/^get\s+/i, 'Obtain ')
    .replace(/[.!?]+$/, '')
    .trim()

  if (!cleaned) {
    cleaned = clause
  }

  // Cap at 8 words / 60 chars so it reads as a task title, never a raw transcript dump
  const words = cleaned.split(/\s+/)
  if (words.length > 8) {
    cleaned = words
      .slice(0, 8)
      .join(' ')
      .replace(/[,;:-]+$/, '')
  }
  if (cleaned.length > 60) {
    cleaned = cleaned.slice(0, 57).trim()
  }

  if (!cleaned) return ''

  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
}

// Local clinical entity parser so any dictated or edited transcript extracts immediately
export function parseClinicalEntitiesFromTranscript(rawTranscript) {
  const clean = (rawTranscript || '').trim()
  if (!clean) {
    return {
      appointmentNotes: '',
      billableActivities: [],
      workStatusUpdate: null,
      proposedTasks: [],
      nextAppointment: null,
      contacts: [],
    }
  }

  const lower = clean.toLowerCase()

  // 1. Appointment Notes
  const appointmentNotes = `<p>${clean}</p>`

  // 2. Billable Activities
  const billableActivities = []
  const sentences = clean.split(/(?<=[.!?])\s+|\n+/)

  sentences.forEach(sentence => {
    const sLower = sentence.toLowerCase()
    // Check if sentence mentions travel/drive
    if (/\b(travel|drove|driving|round trip|mileage|commute)\b/i.test(sLower)) {
      const time = parseSpokenHours(sLower) || 0.5
      billableActivities.push({
        type: 'Travel',
        description: sentence.replace(/\.$/, '').trim(),
        time,
      })
    } else if (
      /\b(phone|called|spoke with|talked to|conferred|telephonic)\b/i.test(sLower) &&
      parseSpokenHours(sLower) > 0
    ) {
      billableActivities.push({
        type: 'Phone Call',
        description: sentence.replace(/\.$/, '').trim(),
        time: parseSpokenHours(sLower),
      })
    } else if (
      /\b(reviewed|record review|medical records|imaging review)\b/i.test(sLower) &&
      parseSpokenHours(sLower) > 0
    ) {
      billableActivities.push({
        type: 'Record Review',
        description: sentence.replace(/\.$/, '').trim(),
        time: parseSpokenHours(sLower),
      })
    } else if (/\b(spent|attended|appointment|visit|clinic|exam|met with|consult)\b/i.test(sLower)) {
      const time = parseSpokenHours(sLower)
      if (time > 0) {
        billableActivities.push({
          type: 'Coordination of Services',
          description: sentence.replace(/\.$/, '').trim(),
          time,
        })
      }
    }
  })

  // If sentence contained both clinic time and travel time separated by comma (e.g. "Spent 1.2 hours at the clinic, travel was 0.8 hours round trip")
  const clinicClause = lower.match(
    /(?:spent|attended|waited)\s+([^,.]+?)\s+(?:at the clinic|in clinic|at clinic|with patient|in exam)/i,
  )
  const travelClause = lower.match(/travel\s+(?:was\s+|time\s+)?([^,.]+?)(?:\s+round trip|\.|,|$)/i)
  if (clinicClause && travelClause) {
    const clinicTime = parseSpokenHours(clinicClause[1])
    const travelTime = parseSpokenHours(travelClause[1])
    if (clinicTime > 0 && travelTime > 0) {
      billableActivities.length = 0
      billableActivities.push(
        {
          type: 'Coordination of Services',
          description: 'Clinic Attendance & Care Coordination',
          time: clinicTime,
        },
        {
          type: 'Travel',
          description: 'Round trip travel to clinic',
          time: travelTime,
        },
      )
    }
  } else if (billableActivities.length === 0) {
    const anyTime = parseSpokenHours(lower)
    if (anyTime > 0) {
      billableActivities.push({
        type: 'Coordination of Services',
        description: 'Case management clinical coordination',
        time: anyTime,
      })
    }
  }

  // 3. Work Status Update
  let workStatusUpdate = null
  const todayIso = new Date().toISOString().slice(0, 10)
  const restrictionMatch = clean.match(
    /(?:no lifting[^.;,]+|lifting limit[^.;,]+|no prolonged[^.;,]+|restrictions?[:\s]+[^.;]+|with no [^.;]+)/i,
  )
  if (/\b(light duty|modified duty|transitional duty|sedentary duty)\b/i.test(lower)) {
    workStatusUpdate = {
      workStatus: 'Modified duty',
      restrictions: restrictionMatch ? restrictionMatch[0].trim() : 'Modified / Light duty per physician',
      effectiveDate: todayIso,
    }
  } else if (/\b(full duty|regular duty|cleared for full|released to full)\b/i.test(lower)) {
    workStatusUpdate = {
      workStatus: 'Full duty',
      restrictions: 'None (Full duty release)',
      effectiveDate: todayIso,
    }
  } else if (/\b(off work|unable to work|temporarily disabled|total temporary disability|ttd)\b/i.test(lower)) {
    workStatusUpdate = {
      workStatus: 'Off work',
      restrictions: restrictionMatch ? restrictionMatch[0].trim() : 'Off work per physician order',
      effectiveDate: todayIso,
    }
  }

  // 4. Proposed Follow-Up Tasks
  const proposedTasks = []
  const taskClauses =
    sentences.length === 1 && clean.length > 70
      ? clean.split(
          /(?:,\s*and\s+|\s+and\s+(?=(?:i\s+need|we\s+need|need\s+to|please|request|obtain|schedule|follow\s+up)))/i,
        )
      : sentences

  taskClauses.forEach(sentence => {
    const sLower = sentence.toLowerCase().trim()
    if (
      /^(attended|spent|travel|conferred with)\b/i.test(sLower) &&
      !/\b(need to|needs to|request|obtain|authorization|pre-auth|authorize|schedule|order|submit)\b/i.test(sLower)
    ) {
      return
    }
    if (
      /\b(need to|needs to|needed|needs|require|requires|required|must|request|obtain|authorization|pre-auth|authorize|schedule|order|submit|send|transmit|follow up on|follow up to|coordinate|refer|referral|recommend|recommended|review)\b/i.test(
        sLower,
      ) ||
      /\b(additional\s+[^.]+?\b(?:sessions?|visits?|weeks?)|physical therapy|occupational therapy)\b/i.test(sLower)
    ) {
      let leadDays = 3
      if (/\b(work status|employer|restrictions)\b/i.test(sLower)) leadDays = 1
      else if (/\b(specialist|consult|records|surgical|ur)\b/i.test(sLower)) leadDays = 5
      const dueDateObj = new Date(Date.now() + leadDays * 86400000)
      const due = dueDateObj.toISOString().slice(0, 10)
      const conciseTitle = summarizeTaskTitle(sentence)
      if (conciseTitle.length > 3 && !proposedTasks.some(pt => pt.title.toLowerCase() === conciseTitle.toLowerCase())) {
        proposedTasks.push({
          title: conciseTitle,
          description: sentence.trim(),
          dueDate: due,
          dueDateUtc: `${due}T00:00:00.000Z`,
        })
      }
    }
  })

  // 5. Next Appointment
  let nextAppointment = null
  const followUpMatch = clean.match(
    /(?:next visit|follow[- ]?up|return to clinic|re-?evaluate|see (?:him|her|patient|dr\.?[^.,]+))\s+(?:in\s+)?(\d+)\s*(weeks?|days?|months?)/i,
  )
  const drMatch = clean.match(/\b(Dr\.?\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/)
  if (followUpMatch) {
    const count = parseInt(followUpMatch[1], 10) || 4
    const unit = followUpMatch[2].toLowerCase()
    const daysToAdd = unit.startsWith('week') ? count * 7 : unit.startsWith('month') ? count * 30 : count
    const suggestedDate = new Date(Date.now() + daysToAdd * 86400000).toISOString().slice(0, 10)
    nextAppointment = {
      title: 'Follow-Up Evaluation',
      provider: drMatch ? drMatch[1] : '',
      date: suggestedDate,
      suggestedDate,
      description: followUpMatch[0],
    }
  } else if (/\b(next visit|follow[- ]?up appointment|next appointment)\b/i.test(lower)) {
    const suggestedDate = new Date(Date.now() + 28 * 86400000).toISOString().slice(0, 10)
    nextAppointment = {
      title: 'Follow-Up Evaluation',
      provider: drMatch ? drMatch[1] : '',
      date: suggestedDate,
      suggestedDate,
      description: 'Scheduled follow-up evaluation',
    }
  }

  // 6. Key Contacts
  const contacts = []
  const extMatch = clean.match(/(?:extension|ext\.?)\s*(\d{2,5})/i)
  const phoneMatch = clean.match(/(\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})/)
  const staffMatch = clean.match(
    /(?:medical assistant|assistant|ma|coordinator|adjuster|nurse|therapist|clerk)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i,
  )
  if (staffMatch) {
    const roleMatch = staffMatch[0].match(
      /^(medical assistant|assistant|ma|coordinator|adjuster|nurse|therapist|clerk)/i,
    )
    contacts.push({
      name: staffMatch[1],
      role: roleMatch ? roleMatch[1].replace(/\b\w/g, c => c.toUpperCase()) : 'Clinic Staff',
      extension: extMatch ? extMatch[1] : null,
      phone: phoneMatch ? phoneMatch[1] : null,
      organizationOrProviderName: drMatch ? drMatch[1] : null,
    })
  } else if (drMatch) {
    contacts.push({
      name: drMatch[1],
      role: 'Physician',
      extension: extMatch ? extMatch[1] : null,
      phone: phoneMatch ? phoneMatch[1] : null,
    })
  }

  return {
    transcript: clean,
    appointmentNotes,
    billableActivities,
    workStatusUpdate,
    proposedTasks,
    nextAppointment,
    contacts,
  }
}

const DEFAULT_EXTRACTED_ENTITIES = {
  appointmentNotes:
    '<p>Patient attended follow-up with Dr. Johnson. Knee effusion resolved. Recommended 4 additional weeks PT.</p>',
  billableActivities: [
    { type: 'Coordination of Services', description: 'Clinic Attendance', time: 1.2 },
    { type: 'Travel', description: 'Travel round trip', time: 0.8 },
  ],
  workStatusUpdate: {
    workStatus: 'Modified duty',
    restrictions: 'No lifting > 20 lbs',
    effectiveDate: '2026-10-21',
  },
  proposedTasks: [
    {
      title: 'Request PT Authorization',
      dueDate: '2026-10-24',
      dueDateUtc: '2026-10-24T00:00:00.000Z',
      description: 'Submit 4-week PT auth request',
    },
  ],
  nextAppointment: {
    title: 'Orthopedic Follow-up',
    provider: 'Dr. Johnson',
    date: '2026-11-18',
    description: 'Re-evaluation of knee condition',
  },
  contacts: [
    { name: 'Sarah Jenkins', role: 'Medical Assistant', extension: '204', organizationOrProviderName: 'Ortho West' },
  ],
}

const SpeechReviewModal = ({
  show = true,
  onHide,
  onApply,
  caseNumber = null,
  appointmentId = null,
  initialTranscript = '',
  initialEntities = null,
  simulateMicDenied = false,
  allowBrowserSpeechRecognition = process.env.REACT_APP_ALLOW_BROWSER_SPEECH_RECOGNITION === 'true',
  user = null,
}) => {
  const [isRecording, setIsRecording] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [recordingSeconds, setRecordingSeconds] = useState(0)
  const [audioUrl, setAudioUrl] = useState(null)
  const [permissionError, setPermissionError] = useState(() =>
    simulateMicDenied ? 'NotAllowedError: Microphone access blocked' : null,
  )
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [interimTranscript, setInterimTranscript] = useState('')
  const [extractionStatusMessage, setExtractionStatusMessage] = useState(null)

  const [transcript, setTranscript] = useState(
    initialTranscript ||
      "Attended follow-up evaluation with John Doe and Dr. Johnson at Ortho West. Spent 1.2 hours at the clinic, travel was 0.8 hours round trip. Dr. Johnson noted knee effusion has resolved. Cleared John for light duty with no lifting over 20 pounds. Next visit in 4 weeks. I need to get authorization for 4 more weeks of physical therapy. Conferred with Dr. Johnson's medical assistant Sarah Jenkins at extension 204 regarding medical records.",
  )

  const [entities, setEntities] = useState(() => initialEntities || DEFAULT_EXTRACTED_ENTITIES)
  const [notesHtml, setNotesHtml] = useState(
    () => (initialEntities || DEFAULT_EXTRACTED_ENTITIES).appointmentNotes || '',
  )

  // 6 Categorized entity selection states
  const [selectedCategories, setSelectedCategories] = useState({
    notes: true,
    activities: true,
    workStatus: true,
    tasks: true,
    nextAppointment: true,
    contacts: true,
  })

  const mediaRecorderRef = useRef(null)
  const recognitionRef = useRef(null)
  const transcribeWsRef = useRef(null)
  const prefetchedStreamSessionRef = useRef(null)
  const streamDrainPromiseRef = useRef(null)
  const streamInterruptedRef = useRef(false)
  const streamingAudioCtxRef = useRef(null)
  const scriptProcessorRef = useRef(null)
  const analyserRef = useRef(null)
  const oscilloscopeRafRef = useRef(null)
  const oscilloscopeCanvasRef = useRef(null)
  const isRecordingRef = useRef(false)
  const isPausedRef = useRef(false)
  const skipNextStopExtractRef = useRef(false)
  const liveFinalTranscriptRef = useRef('')
  const latestTranscriptRef = useRef(transcript)
  const lastExtractedTranscriptRef = useRef((transcript || '').trim())
  const audioChunksRef = useRef([])
  const streamRef = useRef(null)
  const timerRef = useRef(null)
  const audioPlayerRef = useRef(null)
  const transcriptTextareaRef = useRef(null)
  const fileInputRef = useRef(null)

  const stopOscilloscope = () => {
    if (oscilloscopeRafRef.current && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(oscilloscopeRafRef.current)
      oscilloscopeRafRef.current = null
    }
    if (analyserRef.current) {
      try {
        analyserRef.current.disconnect()
      } catch (_e) {}
      analyserRef.current = null
    }
  }

  const startOscilloscope = (audioCtx, sourceNode) => {
    if (!audioCtx || !sourceNode || typeof audioCtx.createAnalyser !== 'function') return
    try {
      stopOscilloscope()
      const analyser = audioCtx.createAnalyser()
      analyser.fftSize = 256
      analyser.smoothingTimeConstant = 0.72
      sourceNode.connect(analyser)
      analyserRef.current = analyser

      const bufferLength = analyser.frequencyBinCount
      const dataArray = new Uint8Array(bufferLength)

      const renderFrame = () => {
        if (!isRecordingRef.current) return
        if (typeof requestAnimationFrame === 'function') {
          oscilloscopeRafRef.current = requestAnimationFrame(renderFrame)
        }

        const canvas = oscilloscopeCanvasRef.current
        if (!canvas || typeof canvas.getContext !== 'function') return
        if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent || '')) return
        const ctx = canvas.getContext('2d')
        if (!ctx) return

        const { width, height } = canvas
        ctx.fillStyle = '#0f172a'
        ctx.fillRect(0, 0, width, height)

        // Subtle center baseline
        ctx.strokeStyle = 'rgba(148, 163, 184, 0.25)'
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.moveTo(0, height / 2)
        ctx.lineTo(width, height / 2)
        ctx.stroke()

        if (isPausedRef.current) {
          ctx.strokeStyle = '#fbbf24'
          ctx.lineWidth = 2
          ctx.beginPath()
          ctx.moveTo(0, height / 2)
          ctx.lineTo(width, height / 2)
          ctx.stroke()
          return
        }

        analyser.getByteTimeDomainData(dataArray)

        let maxDeviation = 0
        for (let i = 0; i < bufferLength; i += 1) {
          const dev = Math.abs(dataArray[i] - 128)
          if (dev > maxDeviation) maxDeviation = dev
        }
        const isSpeakingNow = maxDeviation > 4

        ctx.lineWidth = 2
        ctx.strokeStyle = isSpeakingNow ? '#34d399' : '#38bdf8'
        ctx.shadowColor = isSpeakingNow ? 'rgba(52, 211, 153, 0.65)' : 'rgba(56, 189, 248, 0.35)'
        ctx.shadowBlur = isSpeakingNow ? 6 : 2
        ctx.beginPath()

        const sliceWidth = width / bufferLength
        const gain = 2.2
        let x = 0
        for (let i = 0; i < bufferLength; i += 1) {
          const normalized = (dataArray[i] - 128) / 128.0
          const clamped = Math.max(-0.92, Math.min(0.92, normalized * gain))
          const y = height / 2 + clamped * (height / 2)
          if (i === 0) {
            ctx.moveTo(x, y)
          } else {
            ctx.lineTo(x, y)
          }
          x += sliceWidth
        }

        ctx.stroke()
        ctx.shadowBlur = 0
      }

      if (typeof requestAnimationFrame === 'function') {
        oscilloscopeRafRef.current = requestAnimationFrame(renderFrame)
      }
    } catch (_e) {}
  }

  const prefetchStreamingSession = async () => {
    if (allowBrowserSpeechRecognition || !caseNumber || typeof WebSocket === 'undefined') return null
    try {
      const session = await sendRequest({
        url: `/api/cases/${caseNumber}/speech/stream-url?sampleRate=16000`,
        method: 'GET',
      })
      if (session && (session.medicalStreamUrl || session.standardStreamUrl)) {
        prefetchedStreamSessionRef.current = {
          session,
          fetchedAt: Date.now(),
        }
        return session
      }
    } catch (_e) {}
    return null
  }

  const stopAwsTranscribeStreaming = () => {
    stopOscilloscope()
    if (scriptProcessorRef.current) {
      try {
        scriptProcessorRef.current.onaudioprocess = null
        scriptProcessorRef.current.disconnect()
      } catch (_e) {}
      scriptProcessorRef.current = null
    }
    if (streamingAudioCtxRef.current) {
      try {
        streamingAudioCtxRef.current.close()
      } catch (_e) {}
      streamingAudioCtxRef.current = null
    }
    if (transcribeWsRef.current) {
      const ws = transcribeWsRef.current
      transcribeWsRef.current = null
      try {
        ws.onclose = null
        ws.onerror = null
        if (ws.readyState === 1) {
          streamDrainPromiseRef.current = new Promise(resolve => {
            let settled = false
            const finish = () => {
              if (!settled) {
                settled = true
                resolve()
              }
            }
            const timer = setTimeout(() => {
              try {
                ws.close()
              } catch (_e) {}
              finish()
            }, 450)
            if (typeof ws.addEventListener === 'function') {
              ws.addEventListener('close', () => {
                clearTimeout(timer)
                finish()
              })
            }
          })
          ws.send(encodeTranscribeAudioEvent(new Uint8Array(0)))
        } else {
          streamInterruptedRef.current = true
          ws.close()
        }
      } catch (_e) {
        streamInterruptedRef.current = true
      }
    }
  }

  useEffect(() => {
    latestTranscriptRef.current = transcript
  }, [transcript])

  // Programmatic initial focus (zero JSX autoFocus per AGENTS.md) and pre-warm AWS Transcribe WebSocket URL
  useEffect(() => {
    if (show && transcriptTextareaRef.current) {
      transcriptTextareaRef.current.focus()
    }
    if (
      show &&
      caseNumber &&
      !simulateMicDenied &&
      !allowBrowserSpeechRecognition &&
      typeof WebSocket !== 'undefined'
    ) {
      sendRequest({
        url: `/api/cases/${caseNumber}/speech/stream-url?sampleRate=16000`,
        method: 'GET',
      })
        .then(session => {
          if (session && (session.medicalStreamUrl || session.standardStreamUrl)) {
            prefetchedStreamSessionRef.current = {
              session,
              fetchedAt: Date.now(),
            }
          }
        })
        .catch(() => {})
    }
  }, [show, caseNumber, simulateMicDenied, allowBrowserSpeechRecognition])

  // Sync initial entities if prop updates
  useEffect(() => {
    if (initialEntities) {
      setEntities(initialEntities)
      if (initialEntities.appointmentNotes) {
        setNotesHtml(initialEntities.appointmentNotes)
      }
    }
  }, [initialEntities])

  // Clean up media streams, speech recognition, and timer on unmount
  useEffect(() => {
    return () => {
      isRecordingRef.current = false
      if (timerRef.current) clearInterval(timerRef.current)
      if (oscilloscopeRafRef.current && typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(oscilloscopeRafRef.current)
      }
      if (analyserRef.current) {
        try {
          analyserRef.current.disconnect()
        } catch (_e) {}
      }
      if (scriptProcessorRef.current) {
        try {
          scriptProcessorRef.current.onaudioprocess = null
          scriptProcessorRef.current.disconnect()
        } catch (_e) {}
      }
      if (streamingAudioCtxRef.current) {
        try {
          streamingAudioCtxRef.current.close()
        } catch (_e) {}
      }
      if (transcribeWsRef.current) {
        try {
          transcribeWsRef.current.close()
        } catch (_e) {}
      }
      if (recognitionRef.current) {
        try {
          recognitionRef.current.onend = null
          recognitionRef.current.stop()
        } catch (_e) {}
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
      }
    }
  }, [])

  if (!show) return null

  const formatTimer = secs => {
    const mins = Math.floor(secs / 60)
    const remaining = secs % 60
    return `${mins < 10 ? '0' : ''}${mins}:${remaining < 10 ? '0' : ''}${remaining}`
  }

  const mergeServerEntitiesWithLocal = (response, cleanText, localExtracted) => {
    const validWorkStatus =
      response.workStatusUpdate &&
      response.workStatusUpdate.workStatus &&
      !/^(null|none)$/i.test(String(response.workStatusUpdate.workStatus).trim())
        ? response.workStatusUpdate
        : localExtracted.workStatusUpdate

    const validNextAppt =
      response.nextAppointment &&
      response.nextAppointment.title &&
      !/^(null|none)$/i.test(String(response.nextAppointment.title).trim())
        ? response.nextAppointment
        : localExtracted.nextAppointment

    const apiTasks = Array.isArray(response.proposedTasks)
      ? response.proposedTasks.filter(t => t && t.title && String(t.title).trim())
      : []
    const localTasks = Array.isArray(localExtracted.proposedTasks) ? localExtracted.proposedTasks : []
    const combinedTasks = apiTasks.length > 0 ? apiTasks : localTasks

    const normalizedTasks = combinedTasks.map(t => {
      const rawDue = t.dueDate || (t.dueDateUtc ? String(t.dueDateUtc).slice(0, 10) : null)
      const fallbackDue = new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10)
      const dueStr = rawDue || fallbackDue
      const rawTitle = String(t.title || '').trim()
      const needsConciseSummary =
        rawTitle.length > 60 ||
        /^(?:attended|spent|i\s+need\s+to|we\s+need\s+to|need\s+to|patient\s+attended)\b/i.test(rawTitle)
      const finalTitle = needsConciseSummary ? summarizeTaskTitle(rawTitle) : rawTitle
      return {
        ...t,
        title: finalTitle || 'Follow-Up Task',
        description: t.description || rawTitle,
        dueDate: dueStr,
        dueDateUtc: t.dueDateUtc || `${dueStr}T00:00:00.000Z`,
      }
    })

    return {
      transcript: cleanText,
      appointmentNotes: response.appointmentNotes || localExtracted.appointmentNotes,
      billableActivities:
        response.billableActivities && response.billableActivities.length > 0
          ? response.billableActivities
          : localExtracted.billableActivities,
      workStatusUpdate: validWorkStatus,
      proposedTasks: normalizedTasks,
      nextAppointment: validNextAppt,
      contacts: response.contacts && response.contacts.length > 0 ? response.contacts : localExtracted.contacts,
    }
  }

  // Run extraction from transcript (calls backend API if caseNumber is present, with immediate local clinical parser)
  const runEntityExtraction = async textToExtract => {
    const cleanText = (textToExtract !== undefined ? textToExtract : latestTranscriptRef.current || '').trim()
    if (!cleanText) return null

    setIsAnalyzing(true)
    setExtractionStatusMessage('Extracting clinical entities from dictation...')

    // Immediately compute local structured extraction so UI updates without delay
    const localExtracted = parseClinicalEntitiesFromTranscript(cleanText)
    let resolvedEntities = localExtracted
    setEntities(localExtracted)
    setNotesHtml(localExtracted.appointmentNotes || `<p>${cleanText}</p>`)
    lastExtractedTranscriptRef.current = cleanText

    if (caseNumber) {
      try {
        const response = await sendRequest({
          url: `/api/cases/${caseNumber}/speech/extract`,
          method: 'POST',
          data: { transcript: cleanText, appointmentId },
        })
        if (response && typeof response === 'object') {
          const merged = mergeServerEntitiesWithLocal(response, cleanText, localExtracted)
          resolvedEntities = merged
          setEntities(merged)
          if (merged.appointmentNotes) {
            setNotesHtml(merged.appointmentNotes)
          }
        }
      } catch (_err) {
        // Local extraction already applied above
      }
    }

    setIsAnalyzing(false)
    setExtractionStatusMessage('Clinical entities extracted from your transcript.')
    return resolvedEntities
  }

  const convertAudioBlobToWav16kMono = async inputBlob => {
    if (!inputBlob || typeof window === 'undefined') return inputBlob
    const AudioCtx = window.AudioContext || window.webkitAudioContext
    if (!AudioCtx || typeof inputBlob.arrayBuffer !== 'function') return inputBlob

    let audioCtx = null
    try {
      const arrayBuffer = await inputBlob.arrayBuffer()
      if (!arrayBuffer || arrayBuffer.byteLength === 0) return inputBlob
      audioCtx = new AudioCtx()
      const decoded = await audioCtx.decodeAudioData(arrayBuffer.slice(0))
      const targetSampleRate = 16000
      const targetLength = Math.max(1, Math.ceil(decoded.duration * targetSampleRate))

      let renderedBuffer = decoded
      if (typeof window.OfflineAudioContext !== 'undefined') {
        const offlineCtx = new window.OfflineAudioContext(1, targetLength, targetSampleRate)
        const source = offlineCtx.createBufferSource()
        source.buffer = decoded
        source.connect(offlineCtx.destination)
        source.start(0)
        renderedBuffer = await offlineCtx.startRendering()
      }

      const channelData = renderedBuffer.getChannelData(0)
      const sampleRate = renderedBuffer.sampleRate || targetSampleRate
      const numSamples = channelData.length
      const wavBuffer = new ArrayBuffer(44 + numSamples * 2)
      const view = new DataView(wavBuffer)

      const writeStr = (offset, str) => {
        for (let i = 0; i < str.length; i += 1) {
          view.setUint8(offset + i, str.charCodeAt(i))
        }
      }

      writeStr(0, 'RIFF')
      view.setUint32(4, 36 + numSamples * 2, true)
      writeStr(8, 'WAVE')
      writeStr(12, 'fmt ')
      view.setUint32(16, 16, true)
      view.setUint16(20, 1, true)
      view.setUint16(22, 1, true)
      view.setUint32(24, sampleRate, true)
      view.setUint32(28, sampleRate * 2, true)
      view.setUint16(32, 2, true)
      view.setUint16(34, 16, true)
      writeStr(36, 'data')
      view.setUint32(40, numSamples * 2, true)

      let offset = 44
      for (let i = 0; i < numSamples; i += 1) {
        const s = Math.max(-1, Math.min(1, channelData[i]))
        view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true)
        offset += 2
      }

      return new Blob([wavBuffer], { type: 'audio/wav' })
    } catch (_e) {
      return inputBlob
    } finally {
      if (audioCtx && typeof audioCtx.close === 'function') {
        try {
          await audioCtx.close()
        } catch (_e) {}
      }
    }
  }

  const runAudioBlobExtraction = async audioBlob => {
    if (!caseNumber || !audioBlob || audioBlob.size === 0) return null

    setIsAnalyzing(true)
    setExtractionStatusMessage('Transcribing audio with AWS Transcribe Medical and extracting clinical entities...')
    try {
      const normalizedBlob = await convertAudioBlobToWav16kMono(audioBlob)
      const mime = (normalizedBlob && normalizedBlob.type) || audioBlob.type || 'audio/webm'
      let fileName = audioBlob.name || 'dictation.webm'
      if (mime.includes('wav')) {
        fileName = 'dictation.wav'
      } else if (!audioBlob.name) {
        if (mime.includes('mp4')) fileName = 'dictation.mp4'
        else if (mime.includes('ogg')) fileName = 'dictation.ogg'
        else if (mime.includes('mp3') || mime.includes('mpeg')) fileName = 'dictation.mp3'
      }

      const formData = new FormData()
      formData.append('audioFile', normalizedBlob, fileName)
      if (appointmentId) {
        formData.append('appointmentId', String(appointmentId))
      }

      const response = await sendRequest({
        url: `/api/cases/${caseNumber}/speech/extract`,
        method: 'POST',
        data: formData,
        headers: { 'Content-Type': undefined },
      })

      if (response && typeof response === 'object' && response.transcript) {
        const serverTranscript = String(response.transcript).trim()
        setTranscript(serverTranscript)
        latestTranscriptRef.current = serverTranscript
        lastExtractedTranscriptRef.current = serverTranscript

        const hasPreExtractedEntities =
          Boolean(response.appointmentNotes) ||
          (Array.isArray(response.billableActivities) && response.billableActivities.length > 0) ||
          Boolean(response.workStatusUpdate) ||
          (Array.isArray(response.proposedTasks) && response.proposedTasks.length > 0) ||
          Boolean(response.nextAppointment) ||
          (Array.isArray(response.contacts) && response.contacts.length > 0)

        if (hasPreExtractedEntities) {
          const localExtracted = parseClinicalEntitiesFromTranscript(serverTranscript)
          const merged = mergeServerEntitiesWithLocal(response, serverTranscript, localExtracted)
          setEntities(merged)
          setNotesHtml(merged.appointmentNotes || `<p>${serverTranscript}</p>`)
          setIsAnalyzing(false)
          setExtractionStatusMessage('Clinical entities extracted from your transcript.')
          return merged
        }

        return await runEntityExtraction(serverTranscript)
      }
      setIsAnalyzing(false)
      setExtractionStatusMessage('Audio recorded. Enter or refine transcript text below to extract entities.')
      return null
    } catch (_err) {
      setIsAnalyzing(false)
      setExtractionStatusMessage(
        'Server-side audio transcription is not configured. Please type or paste your transcript below.',
      )
      return null
    }
  }

  const startSpeechRecognition = () => {
    if (!allowBrowserSpeechRecognition) return
    if (typeof window === 'undefined') return
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SpeechRecognition) return

    try {
      const recognition = new SpeechRecognition()
      recognition.continuous = true
      recognition.interimResults = true
      recognition.lang = 'en-US'

      recognition.onresult = event => {
        let interim = ''
        let finalChunk = ''

        for (let i = event.resultIndex; i < event.results.length; i += 1) {
          const res = event.results[i]
          if (res.isFinal) {
            finalChunk += `${res[0].transcript} `
          } else {
            interim += res[0].transcript
          }
        }

        if (finalChunk) {
          liveFinalTranscriptRef.current = `${liveFinalTranscriptRef.current} ${finalChunk}`.replace(/\s+/g, ' ').trim()
        }
        setInterimTranscript(interim)

        const combined = `${liveFinalTranscriptRef.current} ${interim}`.replace(/\s+/g, ' ').trim()
        if (combined) {
          setTranscript(combined)
          latestTranscriptRef.current = combined
          // Live-update preview cards as user speaks so cards are never stale
          const liveEntities = parseClinicalEntitiesFromTranscript(combined)
          setEntities(liveEntities)
          setNotesHtml(liveEntities.appointmentNotes || `<p>${combined}</p>`)
        }
      }

      recognition.onerror = () => {
        // Ignore transient no-speech errors while recording
      }

      recognition.onend = () => {
        if (isRecordingRef.current && !isPausedRef.current) {
          try {
            recognition.start()
          } catch (_e) {}
        }
      }

      recognition.start()
      recognitionRef.current = recognition
    } catch (_e) {
      // Browser SpeechRecognition unavailable; MediaRecorder still captures audio
    }
  }

  const startAwsTranscribeStreaming = async stream => {
    if (allowBrowserSpeechRecognition) return
    if (!caseNumber || !stream || typeof window === 'undefined' || typeof WebSocket === 'undefined') return
    const AudioCtx = window.AudioContext || window.webkitAudioContext
    if (!AudioCtx) return

    const targetSampleRate = 16000
    const pendingPcmQueue = []
    let activeWs = null
    let receivedAnyEvent = false
    let triedFallback = false

    const markStreamInterrupted = () => {
      if (!isRecordingRef.current) return
      streamInterruptedRef.current = true
      pendingPcmQueue.length = 0
      activeWs = null
      setInterimTranscript('')
      setExtractionStatusMessage(
        'Live stream interrupted — full audio is still recording and will be transcribed when you stop.',
      )
    }

    try {
      const audioCtx = new AudioCtx()
      streamingAudioCtxRef.current = audioCtx
      if (audioCtx.state === 'suspended' && typeof audioCtx.resume === 'function') {
        audioCtx.resume().catch(() => {})
      }

      const source = audioCtx.createMediaStreamSource(stream)
      startOscilloscope(audioCtx, source)
      const processor = audioCtx.createScriptProcessor(1024, 1, 1)
      scriptProcessorRef.current = processor

      const silentGain = audioCtx.createGain()
      silentGain.gain.value = 0
      source.connect(processor)
      processor.connect(silentGain)
      silentGain.connect(audioCtx.destination)

      processor.onaudioprocess = audioEvent => {
        if (!isRecordingRef.current || streamInterruptedRef.current) return
        const inputData = audioEvent.inputBuffer.getChannelData(0)
        const inputRate = audioCtx.sampleRate || 48000
        let pcmBytes
        if (isPausedRef.current) {
          // Send silent PCM keep-alive frames while paused so AWS Transcribe does not close the stream after 15s
          const silentSamples = Math.max(1, Math.round((inputData.length * targetSampleRate) / inputRate))
          pcmBytes = new Uint8Array(silentSamples * 2)
        } else {
          pcmBytes = downsampleFloat32ToInt16Pcm(inputData, inputRate, targetSampleRate)
        }
        if (!pcmBytes || pcmBytes.length === 0) return

        if (activeWs && activeWs.readyState === 1) {
          try {
            activeWs.send(encodeTranscribeAudioEvent(pcmBytes))
          } catch (_e) {
            markStreamInterrupted()
          }
        } else if (!isPausedRef.current) {
          if (pendingPcmQueue.length < 240) {
            pendingPcmQueue.push(pcmBytes)
          } else {
            markStreamInterrupted()
          }
        }
      }

      let session = null
      const cached = prefetchedStreamSessionRef.current
      if (cached && cached.session && Date.now() - cached.fetchedAt < 180000) {
        session = cached.session
        prefetchedStreamSessionRef.current = null
      } else {
        session = await sendRequest({
          url: `/api/cases/${caseNumber}/speech/stream-url?sampleRate=${targetSampleRate}`,
          method: 'GET',
        })
      }

      if (!isRecordingRef.current) {
        return
      }
      if (!session || (!session.medicalStreamUrl && !session.standardStreamUrl)) {
        markStreamInterrupted()
        return
      }

      const handleTranscriptPayload = payload => {
        const results = payload?.Transcript?.Results
        if (!Array.isArray(results) || results.length === 0) return

        let interim = ''
        let finalChunk = ''

        for (let i = 0; i < results.length; i += 1) {
          const res = results[i]
          const altText = (res?.Alternatives && res.Alternatives[0] && res.Alternatives[0].Transcript) || ''
          if (!altText) continue
          if (res.IsPartial) {
            interim = `${interim} ${altText}`.trim()
          } else {
            finalChunk = `${finalChunk} ${altText}`.trim()
          }
        }

        if (finalChunk) {
          liveFinalTranscriptRef.current = `${liveFinalTranscriptRef.current} ${finalChunk}`.replace(/\s+/g, ' ').trim()
        }
        setInterimTranscript(interim)

        const combined = `${liveFinalTranscriptRef.current} ${interim}`.replace(/\s+/g, ' ').trim()
        if (combined) {
          setTranscript(combined)
          latestTranscriptRef.current = combined
          const liveEntities = parseClinicalEntitiesFromTranscript(combined)
          setEntities(liveEntities)
          setNotesHtml(liveEntities.appointmentNotes || `<p>${combined}</p>`)
        }
      }

      const connectWebSocket = urlToConnect => {
        if (!urlToConnect || !isRecordingRef.current) return
        const ws = new WebSocket(urlToConnect)
        ws.binaryType = 'arraybuffer'
        activeWs = ws
        transcribeWsRef.current = ws

        ws.onopen = () => {
          while (pendingPcmQueue.length > 0 && ws.readyState === 1) {
            const chunk = pendingPcmQueue.shift()
            try {
              ws.send(encodeTranscribeAudioEvent(chunk))
            } catch (_e) {
              markStreamInterrupted()
              return
            }
          }
        }

        ws.onmessage = async messageEvent => {
          try {
            let buffer = messageEvent.data
            if (buffer && typeof buffer.arrayBuffer === 'function') {
              buffer = await buffer.arrayBuffer()
            }
            if (!buffer) return
            const decoded = decodeTranscribeEventStreamMessage(buffer)
            if (decoded.headers[':exception-type']) {
              if (
                !receivedAnyEvent &&
                !triedFallback &&
                session.standardStreamUrl &&
                urlToConnect !== session.standardStreamUrl
              ) {
                triedFallback = true
                ws.onclose = null
                ws.onerror = null
                try {
                  ws.close()
                } catch (_e) {}
                connectWebSocket(session.standardStreamUrl)
              } else {
                markStreamInterrupted()
              }
              return
            }
            receivedAnyEvent = true
            if (decoded.payload) {
              handleTranscriptPayload(decoded.payload)
            }
          } catch (_e) {}
        }

        ws.onerror = () => {
          if (
            !receivedAnyEvent &&
            !triedFallback &&
            session.standardStreamUrl &&
            urlToConnect !== session.standardStreamUrl
          ) {
            triedFallback = true
            ws.onclose = null
            ws.onerror = null
            try {
              ws.close()
            } catch (_e) {}
            connectWebSocket(session.standardStreamUrl)
          } else {
            markStreamInterrupted()
          }
        }

        ws.onclose = () => {
          if (activeWs === ws && isRecordingRef.current) {
            markStreamInterrupted()
          }
        }
      }

      connectWebSocket(session.medicalStreamUrl || session.standardStreamUrl)
    } catch (_e) {
      markStreamInterrupted()
    }
  }

  const startRecording = async () => {
    if (simulateMicDenied) {
      setPermissionError('NotAllowedError: Microphone access blocked')
      return
    }

    try {
      setPermissionError(null)
      setExtractionStatusMessage(null)

      if (!navigator?.mediaDevices?.getUserMedia) {
        isRecordingRef.current = true
        setIsRecording(true)
        return
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      // Clear previous sample/old transcript and stale sample entities so new dictation replaces them cleanly!
      streamInterruptedRef.current = false
      liveFinalTranscriptRef.current = ''
      latestTranscriptRef.current = ''
      lastExtractedTranscriptRef.current = ''
      setInterimTranscript('')
      setTranscript('')
      const emptyEntities = parseClinicalEntitiesFromTranscript('')
      setEntities(emptyEntities)
      setNotesHtml('')

      // Start live Speech-to-Text recognition (AWS Transcribe Medical Streaming WebSocket by default)
      isRecordingRef.current = true
      isPausedRef.current = false
      if (allowBrowserSpeechRecognition) {
        startSpeechRecognition()
        const AudioCtx = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext)
        if (AudioCtx) {
          try {
            const audioCtx = new AudioCtx()
            streamingAudioCtxRef.current = audioCtx
            const source = audioCtx.createMediaStreamSource(stream)
            startOscilloscope(audioCtx, source)
          } catch (_e) {}
        }
      } else {
        startAwsTranscribeStreaming(stream)
      }

      if (typeof MediaRecorder !== 'undefined') {
        let mimeType = 'audio/webm'
        if (MediaRecorder.isTypeSupported) {
          if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
            mimeType = 'audio/webm;codecs=opus'
          } else if (MediaRecorder.isTypeSupported('audio/webm')) {
            mimeType = 'audio/webm'
          } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
            mimeType = 'audio/mp4'
          }
        }

        const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
        mediaRecorderRef.current = recorder
        audioChunksRef.current = []

        recorder.ondataavailable = e => {
          if (e.data && e.data.size > 0) {
            audioChunksRef.current.push(e.data)
          }
        }

        recorder.onstop = async () => {
          const audioBlob = new Blob(audioChunksRef.current, { type: mimeType || 'audio/webm' })
          if (typeof URL !== 'undefined' && URL.createObjectURL) {
            const url = URL.createObjectURL(audioBlob)
            setAudioUrl(url)
          }

          if (skipNextStopExtractRef.current) {
            skipNextStopExtractRef.current = false
            return
          }

          if (streamDrainPromiseRef.current) {
            try {
              await streamDrainPromiseRef.current
            } catch (_e) {}
            streamDrainPromiseRef.current = null
          }

          // Automatically extract entities from the newly dictated transcript or recorded audio blob.
          // If the live WebSocket stream dropped or errored mid-recording, always fall back to the full MediaRecorder audio blob so no speech is lost.
          const spokenText = (latestTranscriptRef.current || liveFinalTranscriptRef.current || '').trim()
          if (streamInterruptedRef.current && audioBlob && audioBlob.size > 0) {
            streamInterruptedRef.current = false
            await runAudioBlobExtraction(audioBlob)
          } else if (spokenText) {
            await runEntityExtraction(spokenText)
          } else if (audioBlob && audioBlob.size > 0) {
            await runAudioBlobExtraction(audioBlob)
          }
        }

        recorder.start(250)
      }

      setIsRecording(true)
      setIsPaused(false)
      setRecordingSeconds(0)

      timerRef.current = setInterval(() => {
        setRecordingSeconds(prev => prev + 1)
      }, 1000)
    } catch (err) {
      setPermissionError('NotAllowedError: Microphone access blocked. Please check browser permissions.')
      isRecordingRef.current = false
      setIsRecording(false)
    }
  }

  const stopRecording = (options = {}) => {
    const { skipAutoExtract = false } = options
    if (skipAutoExtract) {
      skipNextStopExtractRef.current = true
    }
    isRecordingRef.current = false
    isPausedRef.current = false
    setInterimTranscript('')

    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }

    stopAwsTranscribeStreaming()
    if (caseNumber && !allowBrowserSpeechRecognition) {
      prefetchStreamingSession()
    }

    if (recognitionRef.current) {
      try {
        recognitionRef.current.onend = null
        recognitionRef.current.stop()
      } catch (_e) {}
      recognitionRef.current = null
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop()
      } catch (_e) {
        // ignore
      }
    } else if (!skipAutoExtract) {
      const spokenText = (latestTranscriptRef.current || liveFinalTranscriptRef.current || '').trim()
      if (spokenText) {
        runEntityExtraction(spokenText)
      }
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }

    setIsRecording(false)
    setIsPaused(false)
  }

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording()
    } else {
      startRecording()
    }
  }

  const togglePause = () => {
    if (!isRecording) return

    const nextPaused = !isPaused
    isPausedRef.current = nextPaused

    if (recognitionRef.current) {
      try {
        if (nextPaused) {
          recognitionRef.current.stop()
        } else {
          recognitionRef.current.start()
        }
      } catch (_e) {}
    }

    if (mediaRecorderRef.current) {
      if (isPaused) {
        mediaRecorderRef.current.resume()
        timerRef.current = setInterval(() => {
          setRecordingSeconds(prev => prev + 1)
        }, 1000)
      } else {
        mediaRecorderRef.current.pause()
        if (timerRef.current) {
          clearInterval(timerRef.current)
          timerRef.current = null
        }
      }
    }
    setIsPaused(nextPaused)
  }

  const handleFileUpload = async e => {
    const file = e.target.files?.[0]
    if (file) {
      if (typeof URL !== 'undefined' && URL.createObjectURL) {
        const url = URL.createObjectURL(file)
        setAudioUrl(url)
      }
      await runAudioBlobExtraction(file)
    }
  }

  const handleActivityTimeChange = (index, delta) => {
    setEntities(prev => {
      const updated = [...(prev.billableActivities || [])]
      if (updated[index]) {
        const current = updated[index].time || 0
        const newTime = Math.max(0.1, Math.round((current + delta) * 10) / 10)
        updated[index] = { ...updated[index], time: newTime }
      }
      return { ...prev, billableActivities: updated }
    })
  }

  const toggleCategory = key => {
    setSelectedCategories(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const handleTaskTitleChange = (index, newTitle) => {
    setEntities(prev => {
      const updated = [...(prev.proposedTasks || [])]
      if (updated[index]) {
        updated[index] = { ...updated[index], title: newTitle }
      }
      return { ...prev, proposedTasks: updated }
    })
  }

  const handleExtractFromTranscript = async () => {
    if (!transcript.trim()) return
    await runEntityExtraction(transcript)
  }

  const handleClearTranscript = () => {
    liveFinalTranscriptRef.current = ''
    latestTranscriptRef.current = ''
    setInterimTranscript('')
    setTranscript('')
    setExtractionStatusMessage(null)
    if (transcriptTextareaRef.current) {
      transcriptTextareaRef.current.focus()
    }
  }

  const totalBillableHours = (entities.billableActivities || []).reduce(
    (sum, act) => sum + (parseFloat(act.time) || 0),
    0,
  )

  const handleApply = async () => {
    const wasRecording = isRecording || isRecordingRef.current
    if (wasRecording) {
      stopRecording({ skipAutoExtract: true })
    }

    const currentSpokenText = (latestTranscriptRef.current || liveFinalTranscriptRef.current || transcript || '').trim()
    let activeEntities = entities
    let activeNotesHtml = notesHtml

    if (streamInterruptedRef.current && audioChunksRef.current.length > 0 && caseNumber) {
      streamInterruptedRef.current = false
      const fallbackBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' })
      const extractedFromAudio = await runAudioBlobExtraction(fallbackBlob)
      if (extractedFromAudio) {
        activeEntities = extractedFromAudio
        const fullText = (latestTranscriptRef.current || extractedFromAudio.transcript || '').trim()
        activeNotesHtml = extractedFromAudio.appointmentNotes || (fullText ? `<p>${fullText}</p>` : '')
      }
    } else if (wasRecording || (currentSpokenText && currentSpokenText !== lastExtractedTranscriptRef.current)) {
      const freshlyExtracted = currentSpokenText
        ? await runEntityExtraction(currentSpokenText)
        : parseClinicalEntitiesFromTranscript('')
      if (freshlyExtracted) {
        activeEntities = freshlyExtracted
        activeNotesHtml = freshlyExtracted.appointmentNotes || (currentSpokenText ? `<p>${currentSpokenText}</p>` : '')
      }
    }

    // Sanitize rich-text notes using DOMPurify before committing
    const sanitizedNotes = isQuillEmpty(activeNotesHtml) ? '' : DOMPurify.sanitize(activeNotesHtml)
    const numericCaseNumber = caseNumber ? parseInt(caseNumber, 10) : 0
    const defaultDueStr = new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10)

    const preparedTasks = (activeEntities.proposedTasks || []).map(t => {
      const dueStr = t.dueDate || (t.dueDateUtc ? String(t.dueDateUtc).slice(0, 10) : defaultDueStr)
      const rawTitle = String(t.title || '').trim()
      const needsConciseSummary =
        rawTitle.length > 60 ||
        /^(?:attended|spent|i\s+need\s+to|we\s+need\s+to|need\s+to|patient\s+attended)\b/i.test(rawTitle)
      const finalTitle = needsConciseSummary ? summarizeTaskTitle(rawTitle) : rawTitle
      return {
        ...t,
        title: finalTitle || 'Follow-Up Task',
        description: t.description || rawTitle,
        caseNumber: t.caseNumber || numericCaseNumber,
        dueDate: dueStr,
        dueDateUtc: t.dueDateUtc || `${dueStr}T00:00:00.000Z`,
      }
    })

    const preparedActivities = (activeEntities.billableActivities || []).map(act => ({
      ...act,
      caseNumber: act.caseNumber || numericCaseNumber,
    }))

    const finalTranscript = currentSpokenText || transcript || activeEntities.transcript || ''

    const preparedEntities = {
      ...activeEntities,
      transcript: finalTranscript,
      appointmentNotes: sanitizedNotes,
      billableActivities: preparedActivities,
      proposedTasks: preparedTasks,
    }

    let appliedViaApi = false

    if (caseNumber && Permissions.can(user, CAPABILITIES.UPDATE_CASE)) {
      try {
        await sendRequest({
          url: `/api/cases/${caseNumber}/speech/apply`,
          method: 'POST',
          data: {
            applyNotes: selectedCategories.notes,
            applyActivities: selectedCategories.activities,
            applyWorkStatus: selectedCategories.workStatus,
            applyTasks: selectedCategories.tasks,
            applyNextAppointment: selectedCategories.nextAppointment,
            applyContacts: selectedCategories.contacts,
            appointmentId,
            entities: preparedEntities,
          },
        })
        appliedViaApi = true
      } catch (_e) {
        // Continue to onApply callback
      }
    }

    const payload = {
      transcript: finalTranscript,
      selectedCategories,
      appointmentId,
      appliedViaApi,
      entities: preparedEntities,
    }

    if (onApply) {
      onApply(payload)
    }
  }

  return (
    <div
      className="modal show d-block speech-review-modal"
      role="dialog"
      aria-modal="true"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.55)', zIndex: 1050, overflowY: 'auto' }}
    >
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          {/* Modal Header */}
          <div className="modal-header d-flex justify-content-between align-items-center">
            <h5 className="modal-title font-weight-bold">
              <FAIcon name="microphone" className="mr-2 text-primary" />
              Smart Speech Dictation & HITL Extraction
            </h5>
            <button type="button" className="close" onClick={onHide} aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          {/* Modal Body */}
          <div className="modal-body">
            {/* Permission Warning Banner */}
            {permissionError && (
              <div className="alert alert-warning mic-permission-warning" role="alert">
                <FAIcon name="exclamation-triangle" className="mr-2" />
                <strong>Microphone access blocked.</strong> Please check your browser permissions or paste transcript
                text below.
              </div>
            )}

            {/* Audio Recording & Dictation Controls */}
            <div className="dictation-controls card p-3 mb-3 bg-light border-0">
              <div className="d-flex flex-wrap align-items-center" style={{ gap: '10px' }}>
                <button
                  type="button"
                  className={`btn ${isRecording ? 'btn-danger' : 'btn-primary'} btn-record d-flex align-items-center`}
                  onClick={toggleRecording}
                >
                  <FAIcon name={isRecording ? 'stop-circle' : 'microphone'} className="mr-2" />
                  {isRecording ? 'Stop Dictation' : 'Start Dictation'}
                </button>

                {isRecording && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-pause d-flex align-items-center"
                    onClick={togglePause}
                  >
                    <FAIcon name={isPaused ? 'play' : 'pause'} className="mr-2" />
                    {isPaused ? 'Resume' : 'Pause'}
                  </button>
                )}

                {isRecording && (
                  <span className="badge badge-danger p-2 recording-timer d-flex align-items-center">
                    <span
                      className="recording-pulse mr-2"
                      style={{
                        display: 'inline-block',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#fff',
                      }}
                    />
                    {formatTimer(recordingSeconds)} — Listening (speak now)...
                  </span>
                )}

                {isRecording && (
                  <canvas
                    ref={oscilloscopeCanvasRef}
                    data-testid="speech-oscilloscope"
                    className="speech-oscilloscope rounded"
                    width={200}
                    height={34}
                    title="Live microphone voice oscilloscope"
                    style={{
                      backgroundColor: '#0f172a',
                      border: '1px solid #334155',
                      boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.5)',
                    }}
                  />
                )}

                {/* Upload Audio Fallback */}
                <input
                  type="file"
                  accept="audio/*"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm upload-audio-btn"
                  onClick={() => fileInputRef.current?.click()}
                  title="Upload pre-recorded audio file"
                >
                  <FAIcon name="upload" className="mr-1" /> Upload Audio
                </button>

                {/* HTML5 Audio Player */}
                <div className="ml-auto d-flex align-items-center">
                  <audio
                    ref={audioPlayerRef}
                    controls
                    data-testid="speech-audio-player"
                    className="speech-audio-player"
                    src={audioUrl || undefined}
                  >
                    <track kind="captions" />
                  </audio>
                </div>
              </div>
            </div>

            {/* Editable Transcript Section */}
            <div className="transcript-section card p-3 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label htmlFor="transcriptTextarea" className="font-weight-bold mb-0">
                  <FAIcon name="file-alt" className="mr-2 text-secondary" />
                  Spoken Transcript
                  {isRecording && <span className="badge badge-danger ml-2">Live Speech-to-Text Active</span>}
                </label>
                <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm clear-transcript-btn"
                    onClick={handleClearTranscript}
                    disabled={isAnalyzing || !transcript}
                    title="Clear transcript text"
                  >
                    <FAIcon name="eraser" className="mr-1" />
                    Clear
                  </button>
                  <button
                    type="button"
                    className="btn btn-info btn-sm re-extract-btn font-weight-bold"
                    onClick={handleExtractFromTranscript}
                    disabled={isAnalyzing || !transcript.trim()}
                  >
                    <FAIcon name="sync" className={`mr-1 ${isAnalyzing ? 'fa-spin' : ''}`} />
                    {isAnalyzing ? 'Extracting...' : 'Re-Extract Entities'}
                  </button>
                </div>
              </div>
              <textarea
                id="transcriptTextarea"
                ref={transcriptTextareaRef}
                className="form-control transcript-textarea"
                rows={3}
                value={transcript}
                onChange={e => {
                  const nextVal = e.target.value
                  setTranscript(nextVal)
                  latestTranscriptRef.current = nextVal
                  const liveEntities = parseClinicalEntitiesFromTranscript(nextVal)
                  setEntities(liveEntities)
                  setNotesHtml(liveEntities.appointmentNotes || (nextVal.trim() ? `<p>${nextVal.trim()}</p>` : ''))
                }}
                placeholder={
                  isRecording
                    ? 'Listening to microphone... Speak your appointment notes, time spent, restrictions, and follow-up tasks...'
                    : 'Click "Start Dictation" to speak, or type/paste your transcript here and click "Re-Extract Entities"...'
                }
              />
              {interimTranscript && isRecording && (
                <small className="text-primary mt-1 d-block font-italic">
                  Hearing: &ldquo;{interimTranscript}&rdquo;
                </small>
              )}
              {extractionStatusMessage && isRecording && (
                <small className="text-warning mt-1 d-block font-weight-bold">
                  <FAIcon name="exclamation-circle" className="mr-1" />
                  {extractionStatusMessage}
                </small>
              )}
              {extractionStatusMessage && !isRecording && (
                <small className={`${isAnalyzing ? 'text-info' : 'text-success'} mt-1 d-block font-weight-bold`}>
                  <FAIcon
                    name={isAnalyzing ? 'spinner' : 'check-circle'}
                    className={`mr-1 ${isAnalyzing ? 'fa-spin' : ''}`}
                  />
                  {extractionStatusMessage}
                </small>
              )}
            </div>

            {/* 6 Categorized Clinical Review Cards */}
            <div className="categorized-cards-grid row">
              {/* 1. Appointment Notes Card */}
              <div className="col-md-6 mb-3">
                <div className="card h-100 entity-review-card notes-card border">
                  <div className="card-header bg-white d-flex justify-content-between align-items-center">
                    <label className="mb-0 font-weight-bold d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={selectedCategories.notes}
                        onChange={() => toggleCategory('notes')}
                      />
                      Appointment Notes
                    </label>
                    <span className="badge badge-primary">Clinical Note</span>
                  </div>
                  <div className="card-body">
                    <div
                      className="notes-preview-content p-2 bg-light rounded"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(notesHtml) }}
                    />
                  </div>
                </div>
              </div>

              {/* 2. Billable Activities Card */}
              <div className="col-md-6 mb-3">
                <div className="card h-100 entity-review-card activities-card border">
                  <div className="card-header bg-white d-flex justify-content-between align-items-center">
                    <label className="mb-0 font-weight-bold d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={selectedCategories.activities}
                        onChange={() => toggleCategory('activities')}
                      />
                      Billable Activities ({totalBillableHours.toFixed(1)} hrs)
                    </label>
                    <span className="badge badge-success">{totalBillableHours.toFixed(1)} hrs</span>
                  </div>
                  <div className="card-body">
                    {(entities.billableActivities || []).length > 0 ? (
                      <ul className="list-unstyled mb-0">
                        {(entities.billableActivities || []).map((act, i) => (
                          <li
                            key={i}
                            className="mb-2 d-flex justify-content-between align-items-center p-2 bg-light rounded"
                          >
                            <div>
                              <strong>{act.type}</strong>: {act.description}
                            </div>
                            <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm p-0 px-1"
                                onClick={() => handleActivityTimeChange(i, -0.1)}
                                title="Decrease 0.1 hr"
                              >
                                -
                              </button>
                              <span className="badge badge-dark px-2 py-1">{act.time} hr</span>
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm p-0 px-1"
                                onClick={() => handleActivityTimeChange(i, 0.1)}
                                title="Increase 0.1 hr"
                              >
                                +
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-muted mb-0">No billable durations detected in transcript</p>
                    )}
                  </div>
                </div>
              </div>

              {/* 3. Work Status Card */}
              <div className="col-md-6 mb-3">
                <div className="card h-100 entity-review-card work-status-card border">
                  <div className="card-header bg-white d-flex justify-content-between align-items-center">
                    <label className="mb-0 font-weight-bold d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={selectedCategories.workStatus}
                        onChange={() => toggleCategory('workStatus')}
                      />
                      Work Status Update
                    </label>
                    <span className="badge badge-warning">Restrictions</span>
                  </div>
                  <div className="card-body">
                    {entities.workStatusUpdate ? (
                      <p className="mb-0">
                        <strong>Status:</strong> {entities.workStatusUpdate.workStatus}
                        <br />
                        <strong>Restrictions:</strong> {entities.workStatusUpdate.restrictions || 'None stated'}
                        {entities.workStatusUpdate.effectiveDate && (
                          <>
                            <br />
                            <strong>Effective Date:</strong> {entities.workStatusUpdate.effectiveDate}
                          </>
                        )}
                      </p>
                    ) : (
                      <p className="text-muted mb-0">No work status change detected</p>
                    )}
                  </div>
                </div>
              </div>

              {/* 4. Follow-Up Tasks Card */}
              <div className="col-md-6 mb-3">
                <div className="card h-100 entity-review-card tasks-card border">
                  <div className="card-header bg-white d-flex justify-content-between align-items-center">
                    <label className="mb-0 font-weight-bold d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={selectedCategories.tasks}
                        onChange={() => toggleCategory('tasks')}
                      />
                      Follow-Up Tasks
                    </label>
                    <span className="badge badge-info">{(entities.proposedTasks || []).length} Task(s)</span>
                  </div>
                  <div className="card-body">
                    {(entities.proposedTasks || []).length > 0 ? (
                      entities.proposedTasks.map((t, i) => (
                        <div key={i} className="mb-2 p-2 bg-light rounded">
                          <strong className="d-block mb-1">{t.title}</strong>
                          <input
                            type="text"
                            className="form-control form-control-sm mb-1 proposed-task-title-input"
                            aria-label={`Follow-up task ${i + 1} title`}
                            value={t.title || ''}
                            onChange={e => handleTaskTitleChange(i, e.target.value)}
                          />
                          {t.description && t.description !== t.title && (
                            <small className="text-secondary d-block mb-1">{t.description}</small>
                          )}
                          <small className="text-muted">Due: {t.dueDate || t.dueDateUtc || 'Standard lead-time'}</small>
                        </div>
                      ))
                    ) : (
                      <p className="text-muted mb-0">No tasks inferred</p>
                    )}
                  </div>
                </div>
              </div>

              {/* 5. Next Appointment Card */}
              <div className="col-md-6 mb-3">
                <div className="card h-100 entity-review-card next-appt-card border">
                  <div className="card-header bg-white d-flex justify-content-between align-items-center">
                    <label className="mb-0 font-weight-bold d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={selectedCategories.nextAppointment}
                        onChange={() => toggleCategory('nextAppointment')}
                      />
                      Next Appointment
                    </label>
                    <span className="badge badge-secondary">Scheduling</span>
                  </div>
                  <div className="card-body">
                    {entities.nextAppointment ? (
                      <p className="mb-0">
                        <strong>{entities.nextAppointment.title}</strong>
                        {entities.nextAppointment.provider && ` with ${entities.nextAppointment.provider}`}
                        {(entities.nextAppointment.date || entities.nextAppointment.suggestedDate) &&
                          ` on ${String(entities.nextAppointment.date || entities.nextAppointment.suggestedDate).slice(
                            0,
                            10,
                          )}`}
                      </p>
                    ) : (
                      <p className="text-muted mb-0">No follow-up visit suggested</p>
                    )}
                  </div>
                </div>
              </div>

              {/* 6. Key Contacts Card */}
              <div className="col-md-6 mb-3">
                <div className="card h-100 entity-review-card contacts-card border">
                  <div className="card-header bg-white d-flex justify-content-between align-items-center">
                    <label className="mb-0 font-weight-bold d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={selectedCategories.contacts}
                        onChange={() => toggleCategory('contacts')}
                      />
                      Key Contacts
                    </label>
                    <span className="badge badge-dark">{(entities.contacts || []).length} Contact(s)</span>
                  </div>
                  <div className="card-body">
                    {(entities.contacts || []).length > 0 ? (
                      entities.contacts.map((c, i) => (
                        <p key={i} className="mb-1 p-2 bg-light rounded">
                          <strong>{c.name}</strong> ({c.role}
                          {c.extension ? `, ext. ${c.extension}` : ''}
                          {c.organizationOrProviderName ? ` - ${c.organizationOrProviderName}` : ''})
                        </p>
                      ))
                    ) : (
                      <p className="text-muted mb-0">No contacts mentioned</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="modal-footer d-flex justify-content-between align-items-center">
            <button type="button" className="btn btn-secondary" onClick={onHide}>
              Cancel
            </button>
            <div className="d-flex align-items-center" style={{ gap: '8px' }}>
              {isRecording && (
                <button
                  type="button"
                  className="btn btn-danger stop-and-review-btn d-flex align-items-center font-weight-bold"
                  onClick={() => stopRecording()}
                >
                  <FAIcon name="stop-circle" className="mr-2" />
                  Stop Dictation &amp; Review
                </button>
              )}
              <button
                type="button"
                className="btn btn-success apply-to-case-btn d-flex align-items-center font-weight-bold"
                onClick={handleApply}
                disabled={isAnalyzing}
              >
                <FAIcon name={isAnalyzing ? 'sync' : 'check'} className={`mr-2 ${isAnalyzing ? 'fa-spin' : ''}`} />
                {isAnalyzing ? 'Extracting & Applying...' : isRecording ? 'Stop & Apply to Case' : 'Apply to Case'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SpeechReviewModal
